/**
 * Kinetipath PDP size-guide modal.
 *
 * Progressive enhancement: the trigger stays a real <a href="/pages/size-guide">.
 * With JS, clicking opens a modal (reusing Dawn's <modal-dialog> for focus trap,
 * ESC, scroll lock and overlay/close) whose body is built from a LIVE fetch of the
 * size-guide page. Without JS, or on any failure, the link simply navigates there.
 *
 * Content source: a full-page fetch of /pages/size-guide parsed with DOMParser, from
 * which we extract the finder + the two size-table section roots. The Section Rendering
 * API ?sections= shortcut can't be used here: sections inside a JSON template require
 * the full theme-instance section IDs (template--<id>__key), which we must not hardcode
 * (they break on theme duplication / republish).
 *
 * No storage — the extracted markup lives in the modal DOM (injected once) for the page
 * session; re-opening just re-shows it (re-injecting would drop the finder's event
 * listeners, since assets/size-guide-finder.js is a one-shot IIFE).
 */
(function () {
  'use strict';

  var trigger = document.querySelector('[data-sg-trigger]');
  if (!trigger) return;

  var section = trigger.closest('[data-section-id]');
  if (!section) return;
  var modalId = 'SizeGuideModal-' + section.getAttribute('data-section-id');

  // Wait until Dawn's <modal-dialog> is defined (and has moved itself to <body>).
  // If it never defines, the click handler is never attached and the link navigates.
  customElements.whenDefined('modal-dialog').then(function () {
    var modal = document.getElementById(modalId);
    if (!modal || typeof modal.show !== 'function') return;

    var body = modal.querySelector('[data-sg-body]');
    var loaded = false;

    // Never replace the loading state with an empty/partial body — bail to the full page.
    function fail() {
      loaded = false;
      window.location.assign(trigger.getAttribute('href') || '/pages/size-guide');
    }

    function loadContent() {
      if (loaded || !body) return;
      loaded = true;

      fetch('/pages/size-guide')
        .then(function (res) {
          if (!res.ok) throw new Error('Size guide fetch failed: ' + res.status);
          return res.text();
        })
        .then(function (html) {
          // Parse without executing scripts, then extract the rendered section roots.
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var widget = doc.querySelector('[data-kp-finder]');
          var finder = widget ? widget.closest('.size-guide-finder') : null;
          var tables = doc.querySelectorAll('.size-guide-table'); // document order: women, men

          // Bail (navigate to the page) unless all three roots are present — no partial modal.
          if (!finder || tables.length < 2) {
            fail();
            return;
          }

          // Inject finder -> women's -> men's as direct <section> children (matches the
          // .sg-modal__body > section + section spacing rule — no CSS change needed).
          body.innerHTML = finder.outerHTML + tables[0].outerHTML + tables[1].outerHTML;

          // The finder's in-modal "measure" anchors target a section not present here —
          // repoint them at the full page (no edit to the size-guide sections).
          var measureLinks = body.querySelectorAll('a[href="#size-measure"]');
          for (var i = 0; i < measureLinks.length; i++) {
            measureLinks[i].setAttribute('href', '/pages/size-guide#size-measure');
          }

          // <script> doesn't execute when inserted as parsed markup. Recreate the finder
          // script from the parsed document so its IIFE hydrates the injected widget.
          // Guard against a second append across re-opens.
          var inert = doc.querySelector('script[src*="size-guide-finder"]');
          if (inert && !document.querySelector('script[data-sg-finder]')) {
            var live = document.createElement('script');
            live.src = inert.getAttribute('src');
            live.defer = true;
            live.setAttribute('data-sg-finder', '');
            document.body.appendChild(live);
          }
        })
        .catch(fail);
    }

    trigger.addEventListener('click', function (event) {
      // Let modified / non-primary clicks (open-in-new-tab, etc.) navigate natively.
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      modal.show(trigger);
      loadContent();
    });
  });
})();
